package br.com.texeratx.boardgametimeapp.ui.screens.expansions.edit

import br.com.texeratx.boardgametimeapp.core.i18n.LanguageManager
import br.com.texeratx.boardgametimeapp.data.model.expansion.UpdateExpansionRequest
import br.com.texeratx.boardgametimeapp.domain.usecases.expansions.GetExpansionByIdUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.expansions.UpdateExpansionUseCase
import br.com.texeratx.boardgametimeapp.domain.usecases.games.GetGamesSimpleUseCase
import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.launch

class EditExpansionViewModel(
    private val getGamesSimpleUseCase: GetGamesSimpleUseCase,
    private val getExpansionByIdUseCase: GetExpansionByIdUseCase,
    private val updateExpansionUseCase: UpdateExpansionUseCase,
    private val lang: LanguageManager
) : ScreenModel {

    val state = MutableStateFlow(EditExpansionState())

    fun load(expansionId: Long) {
        screenModelScope.launch {
            state.value = state.value.copy(
                isLoading = true,
                errorMessage = null,
                uiMessage = null,
                expansionId = expansionId
            )

            val gamesResult = getGamesSimpleUseCase()
            val expansionResult = getExpansionByIdUseCase(expansionId)

            val games = gamesResult.getOrElse {
                state.value = state.value.copy(isLoading = false, errorMessage = it.message ?: lang.strings.value.errorLoadingGames)
                return@launch
            }

            val expansion = expansionResult.getOrElse {
                state.value = state.value.copy(isLoading = false, errorMessage = it.message ?: lang.strings.value.errorLoadingExpansion)
                return@launch
            }

            state.value = state.value.copy(
                games = games,
                selectedGameId = expansion.gameId,
                name = expansion.name,
                imageUrl = expansion.imageUrl.orEmpty(),
                bggUrl = expansion.bggUrl.orEmpty(),
                isLoading = false
            )
        }
    }

    fun onNameChange(v: String) {
        state.value = state.value.copy(name = v)
    }

    fun onImageUrlChange(v: String) {
        state.value = state.value.copy(imageUrl = v)
    }

    fun onBggUrlChange(v: String) {
        state.value = state.value.copy(bggUrl = v)
    }

    fun onGameSelected(gameId: Long) {
        state.value = state.value.copy(selectedGameId = gameId)
    }

    fun save(onSuccess: () -> Unit) {
        val s = state.value

        if (s.name.isBlank()) {
            state.value = s.copy(errorMessage = lang.strings.value.expansionNameRequired)
            return
        }
        if (s.selectedGameId == null) {
            state.value = s.copy(errorMessage = lang.strings.value.selectAGame)
            return
        }

        screenModelScope.launch {
            state.value = s.copy(isSaving = true, errorMessage = null, uiMessage = null)

            val req = UpdateExpansionRequest(
                name = s.name.trim(),
                gameId = s.selectedGameId,
                imageUrl = s.imageUrl.trim().ifBlank { "" }, // permite limpar
                bggUrl = s.bggUrl.trim().ifBlank { "" }
            )

            updateExpansionUseCase(s.expansionId, req)
                .onSuccess {
                    state.value = state.value.copy(isSaving = false, uiMessage = lang.strings.value.expansionUpdatedSuccess)
                    onSuccess()
                }
                .onFailure {
                    state.value = state.value.copy(isSaving = false, errorMessage = it.message ?: lang.strings.value.errorUpdatingExpansion)
                }
        }
    }

    fun consumeMessage() {
        state.value = state.value.copy(uiMessage = null)
    }

    fun clearError() {
        state.value = state.value.copy(errorMessage = null)
    }
}
